/** Aged payables, cash requirements and supplier statement (M05-09). */
import { Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { api } from '../../lib/api';
import { amount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { manilaToday, useContacts } from '../../lib/queries';
import { Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, TableWrap, Td, Th, filterClass } from '../../components/ui';

const BUCKETS = [['current', 'Current'], ['d1_30', '1–30'], ['d31_60', '31–60'], ['d61_90', '61–90'], ['d91_120', '91–120'], ['over120', 'Over 120']] as const;

interface Aging {
  rows: { contactId: string; supplier: string; total: string; buckets: Record<string, string>; items: { id: string; number: string; supplierInvoiceNo: string; dueDate: string | null; balance: string; daysPastDue: number }[] }[];
  totals: Record<string, string>;
}

export function AgedPayablesPage(): JSX.Element {
  const [asOf, setAsOf] = useState(manilaToday());
  const [open, setOpen] = useState<string | null>(null);
  const aging = useQuery({ queryKey: ['ap-aging', asOf], queryFn: () => api<Aging>(`/reports/aged-payables?asOf=${asOf}`) });
  return (
    <>
      <PageHeader title="Aged Payables" description="What you owe suppliers, by how long it has been due." />
      <FilterBar><label className="text-sm text-slate-600 dark:text-slate-300">As of <input type="date" className={`${filterClass} ml-1`} value={asOf} onChange={(e) => setAsOf(e.target.value)} /></label></FilterBar>
      <Card>
        {aging.isLoading && <Skeleton className="h-48 w-full" />}
        {aging.data?.rows.length === 0 && <EmptyState title="Nothing owed" />}
        {aging.data && aging.data.rows.length > 0 && (
          <TableWrap>
            <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Supplier</Th>{BUCKETS.map(([, l]) => <Th key={l} align="right">{l}</Th>)}<Th align="right">Total</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {aging.data.rows.map((r) => (
                <Fragment key={r.contactId}>
                  <tr>
                    <Td>
                      <button className="inline-flex items-center gap-1 font-medium" onClick={() => setOpen(open === r.contactId ? null : r.contactId)} aria-expanded={open === r.contactId}><ChevronDown aria-hidden="true" className={`h-4 w-4 transition ${open === r.contactId ? 'rotate-180' : ''}`} /> {r.supplier}</button>
                      <Link className="ml-3 text-xs text-brand-700 hover:underline dark:text-brand-100" to={`/reports/supplier-statement?contact=${r.contactId}`}>Statement</Link>
                    </Td>
                    {BUCKETS.map(([k]) => <Td key={k} align="right"><Money>{amount(r.buckets[k], true)}</Money></Td>)}
                    <Td align="right"><Money className="font-semibold">{amount(r.total)}</Money></Td>
                  </tr>
                  {open === r.contactId && r.items.map((i) => (
                    <tr key={i.id} className="bg-slate-50/60 text-xs dark:bg-slate-800/30">
                      <Td className="pl-10"><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/purchases/${i.id}`}>{i.number}</Link> · {i.supplierInvoiceNo} · due {shortDate(i.dueDate)}</Td>
                      <Td align="right" className="text-slate-500">{i.daysPastDue > 0 ? `${i.daysPastDue} days late` : 'not due'}</Td>
                      <Td>{''}</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td><Td>{''}</Td>
                      <Td align="right"><Money>{amount(i.balance)}</Money></Td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot><tr className="border-t-2 border-slate-300 font-bold dark:border-slate-700"><Td>Total</Td>{BUCKETS.map(([k]) => <Td key={k} align="right"><Money>{amount(aging.data!.totals[k], true)}</Money></Td>)}<Td align="right"><Money>{amount(aging.data.totals.total)}</Money></Td></tr></tfoot>
          </TableWrap>
        )}
      </Card>
    </>
  );
}

interface Cash {
  overdue: string;
  later: string;
  weeks: { from: string; to: string; total: string; bills: { id: string; number: string; supplier: string; dueDate: string; balance: string }[] }[];
}

export function CashRequirementsPage(): JSX.Element {
  const [from, setFrom] = useState(manilaToday());
  const cash = useQuery({ queryKey: ['cash-req', from], queryFn: () => api<Cash>(`/reports/cash-requirements?from=${from}`) });
  return (
    <>
      <PageHeader title="Cash Requirements" description="Bills falling due, week by week, so there is money in the bank when they are." />
      <FilterBar><label className="text-sm text-slate-600 dark:text-slate-300">Starting <input type="date" className={`${filterClass} ml-1`} value={from} onChange={(e) => setFrom(e.target.value)} /></label></FilterBar>
      {cash.isLoading && <Skeleton className="h-48 w-full" />}
      {cash.data && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Card><p className="text-sm text-slate-500">Already overdue</p><p className="text-2xl font-bold text-red-600 tabular-nums">{amount(cash.data.overdue)}</p></Card>
            <Card><p className="text-sm text-slate-500">Due after these 8 weeks</p><p className="text-2xl font-bold tabular-nums">{amount(cash.data.later)}</p></Card>
          </div>
          <Card>
            <TableWrap>
              <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Week</Th><Th>Bills</Th><Th align="right">Due</Th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {cash.data.weeks.map((w) => (
                  <tr key={w.from}>
                    <Td className="whitespace-nowrap">{shortDate(w.from)} – {shortDate(w.to)}</Td>
                    <Td className="text-xs">{w.bills.map((b) => <Link key={b.id} className="mr-3 text-brand-700 hover:underline dark:text-brand-100" to={`/purchases/${b.id}`}>{b.supplier} {amount(b.balance)}</Link>)}</Td>
                    <Td align="right"><Money className="font-semibold">{amount(w.total, true)}</Money></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </Card>
        </div>
      )}
    </>
  );
}

interface Statement {
  opening: string;
  closing: string;
  lines: { date: string; kind: string; number: string; charge: string; payment: string; balance: string; id: string; note: string }[];
}

export function SupplierStatementPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const suppliers = useContacts('supplier');
  const today = manilaToday();
  const [contactId, setContactId] = useState(params.get('contact') ?? '');
  const [from, setFrom] = useState(`${today.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(today);
  const s = useQuery({ queryKey: ['supplier-soa', contactId, from, to], queryFn: () => api<Statement>(`/reports/supplier-statement/${contactId}?from=${from}&to=${to}`), enabled: Boolean(contactId) });
  const KIND: Record<string, string> = { BILL: 'Bill', SUPPLIER_CREDIT: 'Supplier credit', PAYMENT: 'Payment', VOID: 'Voided', PAYMENT_VOID: 'Payment voided' };
  return (
    <>
      <PageHeader title="Supplier Statement" description="Reconcile against the statement your supplier sends you." />
      <FilterBar>
        <select className={`${filterClass} w-72`} value={contactId} onChange={(e) => { setContactId(e.target.value); setParams({ contact: e.target.value }, { replace: true }); }} aria-label="Supplier">
          <option value="">Choose a supplier</option>
          {(suppliers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.registeredName}</option>)}
        </select>
        <label className="text-sm text-slate-600 dark:text-slate-300">From <input type="date" className={`${filterClass} ml-1`} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-sm text-slate-600 dark:text-slate-300">To <input type="date" className={`${filterClass} ml-1`} value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </FilterBar>
      <Card>
        {!contactId && <EmptyState title="Choose a supplier" />}
        {s.isLoading && <Skeleton className="h-40 w-full" />}
        {s.data && (
          <>
            <div className="mb-4 flex gap-6 text-sm"><span><span className="text-slate-500">Opening</span> <Money className="font-semibold">{amount(s.data.opening)}</Money></span><span><span className="text-slate-500">Owed now</span> <Money className="font-semibold">{amount(s.data.closing)}</Money></span></div>
            {s.data.lines.length === 0 ? <EmptyState title="No activity" /> : (
              <TableWrap>
                <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Date</Th><Th>Document</Th><Th>Number</Th><Th align="right">Billed</Th><Th align="right">Paid / credited</Th><Th align="right">Balance</Th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {s.data.lines.map((l, i) => (
                    <tr key={`${l.id}-${i}`}>
                      <Td className="whitespace-nowrap">{shortDate(l.date)}</Td>
                      <Td>{KIND[l.kind] ?? l.kind}{l.note && <span className="block text-xs text-slate-500">{l.note}</span>}</Td>
                      <Td className="whitespace-nowrap"><Link className="text-brand-700 hover:underline dark:text-brand-100" to={l.kind.startsWith('PAYMENT') ? `/payments/${l.id}` : `/purchases/${l.id}`}>{l.number}</Link></Td>
                      <Td align="right"><Money>{amount(l.charge, true)}</Money></Td>
                      <Td align="right"><Money>{amount(l.payment, true)}</Money></Td>
                      <Td align="right"><Money>{amount(l.balance)}</Money></Td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            )}
          </>
        )}
      </Card>
    </>
  );
}
